/* eslint no-console: 0 */
import * as types from '../mutation-types';
import Report from '../../api/reports';
import { downloadCsvFile, generateFileName } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';
import {
  reconcileHeatmapData,
  clampDataBetweenTimeline,
} from 'shared/helpers/ReportsDataHelper';

const state = {
  fetchingStatus: false,
  accountReport: {
    isFetching: {
      conversations_count: false,
      incoming_messages_count: false,
      outgoing_messages_count: false,
      avg_first_response_time: false,
      avg_assigned_first_response_time: false,
      avg_resolution_time: false,
      resolutions_count: false,
      auto_resolves_count: false,
      bot_resolutions_count: false,
      bot_handoffs_count: false,
      reply_time: false,
      first_time_customer_conversations_count: false,
      returning_customer_conversations_count: false,
    },
    data: {
      conversations_count: [],
      incoming_messages_count: [],
      outgoing_messages_count: [],
      avg_first_response_time: [],
      avg_assigned_first_response_time: [],
      avg_resolution_time: [],
      resolutions_count: [],
      auto_resolves_count: [],
      bot_resolutions_count: [],
      bot_handoffs_count: [],
      reply_time: [],
      first_time_customer_conversations_count: [],
      returning_customer_conversations_count: [],
    },
  },
  accountSummary: {
    avg_first_response_time: 0,
    avg_assigned_first_response_time: 0,
    avg_resolution_time: 0,
    conversations_count: 0,
    incoming_messages_count: 0,
    outgoing_messages_count: 0,
    reply_time: 0,
    resolutions_count: 0,
    auto_resolves_count: 0,
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    first_time_customer_conversations_count: 0,
    returning_customer_conversations_count: 0,
    previous: {},
  },
  botSummary: {
    bot_resolutions_count: 0,
    bot_handoffs_count: 0,
    previous: {},
  },
  overview: {
    uiFlags: {
      isFetchingAccountConversationMetric: false,
      isFetchingAccountConversationsHeatmap: false,
      isFetchingAgentConversationMetric: false,
    },
    accountConversationMetric: {},
    accountConversationHeatmap: [],
    agentConversationMetric: [],
  },
  botMetrics: {
    uiFlags: {
      isFetching: false,
    },
    data: {},
  },
  botIntents: {
    uiFlags: {
      isFetching: false,
    },
    data: [],
    meta: {},
  },
  botIntentReports: {
    uiFlags: {
      isFetching: false,
    },
    data: [],
    meta: {},
  },
  botIntentMetrics: {
    uiFlags: {
      isFetching: false,
    },
    data: [],
    meta: {},
  },
};

const getters = {
  getAccountReports(_state) {
    return _state.accountReport;
  },
  getAccountSummary(_state) {
    return _state.accountSummary;
  },
  getBotSummary(_state) {
    return _state.botSummary;
  },
  getAccountConversationMetric(_state) {
    return _state.overview.accountConversationMetric;
  },
  getAccountConversationHeatmapData(_state) {
    return _state.overview.accountConversationHeatmap;
  },
  getAgentConversationMetric(_state) {
    return _state.overview.agentConversationMetric;
  },
  getOverviewUIFlags($state) {
    return $state.overview.uiFlags;
  },
  getBotMetrics(_state) {
    return _state.botMetrics;
  },
  getBotIntents(_state) {
    return _state.botIntents;
  },
  getBotIntentReports(_state) {
    return _state.botIntentReports;
  },
  getBotIntentMetrics(_state) {
    return _state.botIntentMetrics;
  },
};

export const actions = {
  fetchAccountReport({ commit }, reportObj) {
    const { metric } = reportObj;
    commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
      metric,
      value: true,
    });
    Report.getReports(reportObj).then(accountReport => {
      let { data } = accountReport;
      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);
      commit(types.default.SET_ACCOUNT_REPORTS, {
        metric,
        data,
      });
      commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, {
        metric,
        value: false,
      });
    });
  },
  fetchAccountConversationHeatmap({ commit }, reportObj) {
    commit(types.default.TOGGLE_HEATMAP_LOADING, true);

    Report.getReports({
      ...reportObj,
      groupBy: 'hour',
      scope_inbox_id: reportObj.scope_inbox_id,
    }).then(heatmapData => {
      let { data } = heatmapData;

      data = clampDataBetweenTimeline(data, reportObj.from, reportObj.to);

      data = reconcileHeatmapData(data, data);

      commit(types.default.SET_HEATMAP_DATA, data);
      commit(types.default.TOGGLE_HEATMAP_LOADING, false);
    });
  },
  fetchAccountSummary({ commit }, reportObj) {
    Report.getSummary(
      reportObj.from,
      reportObj.to,
      reportObj.type,
      reportObj.id,
      reportObj.groupBy,
      reportObj.businessHours
    )
      .then(accountSummary => {
        commit(types.default.SET_ACCOUNT_SUMMARY, accountSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchBotSummary({ commit }, reportObj) {
    Report.getBotSummary({
      from: reportObj.from,
      to: reportObj.to,
      groupBy: reportObj.groupBy,
      businessHours: reportObj.businessHours,
      scope_inbox_id: reportObj.scope_inbox_id,
    })
      .then(botSummary => {
        commit(types.default.SET_BOT_SUMMARY, botSummary.data);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_REPORT_LOADING, false);
      });
  },
  fetchAccountConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(
      reportObj.type,
      reportObj.page,
      reportObj.scope_inbox_id
    )
      .then(accountConversationMetric => {
        commit(
          types.default.SET_ACCOUNT_CONVERSATION_METRIC,
          accountConversationMetric.data
        );
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  fetchAgentConversationMetric({ commit }, reportObj) {
    commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, true);
    Report.getConversationMetric(
      reportObj.type,
      reportObj.page,
      reportObj.scope_inbox_id
    )
      .then(agentConversationMetric => {
        commit(
          types.default.SET_AGENT_CONVERSATION_METRIC,
          agentConversationMetric.data
        );
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      })
      .catch(() => {
        commit(types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING, false);
      });
  },
  downloadAgentReports(_, reportObj) {
    return Report.getAgentReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'agent',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadLabelReports(_, reportObj) {
    return Report.getLabelReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'label',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadInboxReports(_, reportObj) {
    return Report.getInboxReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'inbox',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadTeamReports(_, reportObj) {
    return Report.getTeamReports(reportObj)
      .then(response => {
        downloadCsvFile(reportObj.fileName, response.data);
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'team',
          businessHours: reportObj?.businessHours,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  downloadAccountConversationHeatmap(_, reportObj) {
    Report.getConversationTrafficCSV(reportObj.scope_inbox_id)
      .then(response => {
        downloadCsvFile(
          generateFileName({
            type: 'Conversation traffic',
            to: reportObj.to,
          }),
          response.data
        );

        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'conversation_heatmap',
          businessHours: false,
        });
      })
      .catch(error => {
        console.error(error);
      });
  },
  fetchBotMetrics({ commit }, { from, to }) {
    commit(types.default.TOGGLE_BOT_METRICS_LOADING, true);
    Report.getBotMetrics({ from, to })
      .then(response => {
        commit(types.default.SET_BOT_METRICS, response.data);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        commit(types.default.TOGGLE_BOT_METRICS_LOADING, false);
      });
  },
  fetchBotIntents({ commit }, params) {
    commit(types.default.TOGGLE_BOT_INTENTS_LOADING, true);
    Report.getBotIntents(params)
      .then(response => {
        commit(types.default.SET_BOT_INTENTS, response.data);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        commit(types.default.TOGGLE_BOT_INTENTS_LOADING, false);
      });
  },
  fetchBotIntentReports({ commit }, params) {
    commit(types.default.TOGGLE_BOT_INTENT_REPORTS_LOADING, true);
    Report.getBotIntentReports(params)
      .then(response => {
        commit(types.default.SET_BOT_INTENT_REPORTS, response.data);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        commit(types.default.TOGGLE_BOT_INTENT_REPORTS_LOADING, false);
      });
  },
  fetchBotIntentMetrics({ commit }, params) {
    commit(types.default.TOGGLE_BOT_INTENT_METRICS_LOADING, true);
    Report.getBotIntentMetrics(params)
      .then(response => {
        commit(types.default.SET_BOT_INTENT_METRICS, response.data);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        commit(types.default.TOGGLE_BOT_INTENT_METRICS_LOADING, false);
      });
  },
};

const mutations = {
  [types.default.SET_ACCOUNT_REPORTS](_state, { metric, data }) {
    _state.accountReport.data[metric] = data;
  },
  [types.default.SET_HEATMAP_DATA](_state, heatmapData) {
    _state.overview.accountConversationHeatmap = heatmapData;
  },
  [types.default.TOGGLE_ACCOUNT_REPORT_LOADING](_state, { metric, value }) {
    _state.accountReport.isFetching[metric] = value;
  },
  [types.default.TOGGLE_HEATMAP_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationsHeatmap = flag;
  },
  [types.default.SET_ACCOUNT_SUMMARY](_state, summaryData) {
    _state.accountSummary = summaryData;
  },
  [types.default.SET_BOT_SUMMARY](_state, summaryData) {
    _state.botSummary = summaryData;
  },
  [types.default.SET_ACCOUNT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.accountConversationMetric = metricData;
  },
  [types.default.TOGGLE_ACCOUNT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAccountConversationMetric = flag;
  },
  [types.default.SET_AGENT_CONVERSATION_METRIC](_state, metricData) {
    _state.overview.agentConversationMetric = metricData;
  },
  [types.default.TOGGLE_AGENT_CONVERSATION_METRIC_LOADING](_state, flag) {
    _state.overview.uiFlags.isFetchingAgentConversationMetric = flag;
  },
  [types.default.SET_BOT_METRICS](_state, data) {
    _state.botMetrics.data = data;
  },
  [types.default.TOGGLE_BOT_METRICS_LOADING](_state, value) {
    _state.botMetrics.uiFlags.isFetching = value;
  },
  [types.default.SET_BOT_INTENTS](_state, data) {
    _state.botIntents.data = data.data;
    _state.botIntents.meta = data.meta;
  },
  [types.default.TOGGLE_BOT_INTENTS_LOADING](_state, value) {
    _state.botIntents.uiFlags.isFetching = value;
  },
  [types.default.SET_BOT_INTENT_REPORTS](_state, data) {
    _state.botIntentReports.data = data.data;
    _state.botIntentReports.meta = data.meta;
  },
  [types.default.TOGGLE_BOT_INTENT_REPORTS_LOADING](_state, value) {
    _state.botIntentReports.uiFlags.isFetching = value;
  },
  [types.default.SET_BOT_INTENT_METRICS](_state, data) {
    _state.botIntentMetrics.data = data.data;
    _state.botIntentMetrics.meta = data.meta;
  },
  [types.default.TOGGLE_BOT_INTENT_METRICS_LOADING](_state, value) {
    _state.botIntentMetrics.uiFlags.isFetching = value;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
